import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { getReminderBuckets } from "../lib/taskReminders";

const NOTIFIED_STORAGE_KEY = "pmi_notified_task_reminders";

function loadNotifiedSet() {
  try {
    return new Set(JSON.parse(localStorage.getItem(NOTIFIED_STORAGE_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

// Session-based reminders: while the app tab is open, this flags overdue /
// due-today incomplete tasks and fires one browser Notification per task per
// day (deduped via localStorage so re-mounting the nav bar on every page
// navigation doesn't re-notify). No server-side push is involved, so
// reminders only fire while a tab with the app is open.
//
// Pass an already-loaded `tasks` array when the caller has one (TasksPage,
// SchedulePage); omit it to have the hook fetch a lightweight copy itself
// (used by ModuleNavBar so the reminder badge/notifications work from any
// module, not just the Tasks/Schedule pages).
export function useTaskReminders(externalTasks) {
  const { t } = useTranslation();
  const [ownTasks, setOwnTasks] = useState([]);
  const notifiedRef = useRef(loadNotifiedSet());
  const tasks = externalTasks ?? ownTasks;

  useEffect(() => {
    if (externalTasks !== undefined) return;
    api.get("/tasks").then(({ data }) => setOwnTasks(data)).catch(() => {});
  }, [externalTasks]);

  useEffect(() => {
    // requestPermission() can reject/throw in browsers that restrict it to
    // direct user gestures (varies by browser/OS) - never let that crash
    // the page, it's a nice-to-have, not a required feature.
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch {
      // ignore - browser doesn't allow/support programmatic permission requests here
    }
  }, []);

  const { overdueTasks, dueTodayTasks, reminderCount } = getReminderBuckets(tasks);

  useEffect(() => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const todayKey = new Date().toISOString().slice(0, 10);
    let changed = false;
    try {
      [...overdueTasks, ...dueTodayTasks].forEach((task) => {
        const notifyKey = `${task.id}_${todayKey}`;
        if (notifiedRef.current.has(notifyKey)) return;
        notifiedRef.current.add(notifyKey);
        changed = true;
        // eslint-disable-next-line no-new
        new Notification(t("reminders.notificationTitle"), {
          body: task.due_date < todayKey ? t("reminders.overdueBody", { title: task.title }) : t("reminders.dueTodayBody", { title: task.title }),
          tag: notifyKey,
        });
      });
      // localStorage can throw (e.g. Safari private browsing, restricted
      // storage contexts) - this is a best-effort dedupe cache, not
      // something worth crashing the whole page over.
      if (changed) localStorage.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify([...notifiedRef.current]));
    } catch (err) {
      console.warn("Task reminder notification failed (non-fatal):", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, t]);

  return { overdueTasks, dueTodayTasks, reminderCount };
}
